import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGeolocated } from "react-geolocated";
import './checkin.scss';
import { authService, jobSiteService } from '../../services/apiService';

const CheckinPage: React.FC = () => {
  const [phoneCode, setPhoneCode] = useState('');
  const [checkedIn, setCheckedIn] = useState<{ [key: string]: any; } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ipAddress, setIpAddress] = useState<string | null>(null);
  const [jobSite, setJobSite] = useState<{ [key: string]: any; } | null>(null);
  const [isOutsideCheckinZone, setIsOutsideCheckinZone] = useState(false);
  const [distanceFromJobSite, setDistanceFromJobSite] = useState<number | null>(null);
  const params = useParams();

  console.log('CheckinPage params:', params);
  console.log('JobSiteId:', params.jobSiteId);
  console.log('TodayDate:', params.todayDate);
  console.log('ForemanId:', params.foremanId);

  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: {
        enableHighAccuracy: false,
      },
      userDecisionTimeout: 5000,
    });

  // Function to calculate distance between two coordinates in meters
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  // Add a state for location warning
  const [showLocationWarning, setShowLocationWarning] = useState(false);

  useEffect(() => {
    const fetchJobSite = async () => {
      try {
        console.log('Fetching job site with ID:', params.jobSiteId);
        console.log('API URL:', process.env.REACT_APP_API_URL);

        const data = await jobSiteService.getJobSite(undefined, Number(params.jobSiteId));
        console.log('JobSite data:', data);
        setJobSite(data);
      } catch (error: unknown) {
        console.error('Detailed error fetching job site:', error);
        // Type guard to check if error is an Error object
        const errorMessage = error instanceof Error
          ? error.message
          : 'An unknown error occurred';
        setError(`Unable to fetch job site information: ${errorMessage}`);
      }
    };

    const fetchIpAddress = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setIpAddress(data.ip);
      } catch (error: unknown) {
        console.error('Error fetching IP address:', error);
        // Don't set error state here as IP is not critical
      }
    };

    if (params.jobSiteId) {
      console.log('Starting job site fetch for ID:', params.jobSiteId);
      fetchJobSite();
      fetchIpAddress();
    } else {
      console.warn('No jobSiteId provided in params');
      setError('No job site ID provided');
    }
  }, [params.jobSiteId]);

  console.log('Got Coords', coords, isGeolocationAvailable, isGeolocationEnabled)

  // Check distance from job site when coordinates and job site data are available
  useEffect(() => {
    if (coords && jobSite && jobSite.latitude && jobSite.longitude) {
      const distance = calculateDistance(
        coords.latitude,
        coords.longitude,
        jobSite.latitude,
        jobSite.longitude
      );

      setDistanceFromJobSite(distance);

      const checkinDistance = Number(process.env.REACT_APP_CHECKON_DISTANCE) || 100; // Default to 100 meters
      setIsOutsideCheckinZone(distance > checkinDistance);

      console.log('Distance from job site:', distance, 'meters');
      console.log('Checkin distance limit:', checkinDistance, 'meters');
      console.log('Outside checkin zone:', distance > checkinDistance);
    }
  }, [coords, jobSite]);

  // Show location warning if geolocation is not available or not enabled
  useEffect(() => {
    if (!isGeolocationAvailable || !isGeolocationEnabled) {
      setShowLocationWarning(true);
    } else {
      setShowLocationWarning(false);
    }
  }, [isGeolocationAvailable, isGeolocationEnabled]);

  const handleCheckin = async (e: React.FormEvent) => {
    console.log('handleCheckin')
    e.preventDefault();
    setError(null);

    try {
      const data = await authService.checkin({
        jobSiteId: Number(params.jobSiteId),
        phoneCode: phoneCode,
        latitude: coords?.latitude,
        longitude: coords?.longitude,
      });

      console.log('Checkin response', data);

      if (data) {
        console.log('setting checked in to', data);
        setCheckedIn(data);

        // The checkin response contains employee data, not user data
        // We don't need to dispatch user data to Redux store for checkin
        // The employee is now checked in and can proceed
      }
    } catch (error) {
      console.error('Checkin error:', error);
      setError('Checkin failed. Please check your phone code.');
    }
  };

  return (
    <div className="checkin-page">
      <div className="checkin-container">
        {showLocationWarning && (
          <div className="warning-message">
            <p>⚠️ Location services are not enabled or you are not near the job site.
              This may affect your ability to properly check in. Please enable location services.</p>
          </div>
        )}

        {checkedIn === null && (
          <form className="checkin-form" onSubmit={handleCheckin}>
            <h2 className="checkin-title">Check-In{jobSite?.name ? ` at ${jobSite.name}` : ''}</h2>
            {error && <p className="error-message">{error}</p>}
            <div className="form-group">
              <label htmlFor="phoneCode">Phone Number / Número de Teléfono</label>
              <input
                type="tel"
                id="phoneCode"
                autoFocus={true}
                value={phoneCode}
                onFocus={() => setError(null)}
                onChange={(e) => setPhoneCode(e.target.value)}
                required
                pattern="[0-9]*"
                inputMode="numeric"
                placeholder="555-1212"
              />
            </div>
            <button type="submit" className="checkin-button">
              Check In
            </button>
          </form>
        )}

        {isOutsideCheckinZone && distanceFromJobSite !== null && (
          <div className="warning-message">
            <p>⚠️ You are {distanceFromJobSite > 1000 ? `${(distanceFromJobSite / 1000).toFixed(1)} km` : `${Math.round(distanceFromJobSite)} meters`} away from the job site.
              You are outside the checkin zone but will still be allowed to check in.</p>
            <p>⚠️ Estás a {distanceFromJobSite > 1000 ? `${(distanceFromJobSite / 1000).toFixed(1)} km` : `${Math.round(distanceFromJobSite)} metros`} del sitio de trabajo.
              Estás fuera de la zona de registro pero aún se te permitirá registrarte.</p>
          </div>
        )}

        {checkedIn?.statusCode >= 300 && (
          <div className="error-message">
            <h2>Error Checking in. {checkedIn?.statusCode}</h2>
            <p>{JSON.stringify(checkedIn)}</p>
          </div>
        )}

        {checkedIn !== null && !checkedIn?.statusCode && (
          <div className="success-message">
            <h2>Welcome {checkedIn.firstName}, you are Checked In!</h2>
            <h3>(¡Bienvenido {checkedIn.firstName}, has registrado tu entrada!)</h3>
          </div>
        )}

        {ipAddress && <p className="ip-address">Your IP: {ipAddress}</p>}
      </div>
    </div>
  );
};

export default CheckinPage;
